import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import RecordProducer from "modules/record/record-producer/components/RecordProducer";
import {
    ConfirmActionDialog,
    FlussPageHeader,
    FlussStatusChip,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { useFlussCable } from "modules/fluss/hooks/useFlussCable";
import { flussApi, flussApiError } from "modules/fluss/services/fluss-api";
import { flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";
import { FlussAnchor } from "modules/fluss/types/fluss";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import { formatConditionSummary, resolveConditionColumns } from "modules/record/record-list/services/conditionSummary";
import { useChoiceLabelMaps } from "modules/record/shared/hooks/useChoiceOptions";
import { recordMutationBus } from "shared/services/recordMutationBus";
import useNotification from "component/global/event-handler/hooks/use-notification";

// PRIM-026 (round 8, Bloco B): the same route the fixed dispatch_form's form_url points at
// (package 4.66.1) -- update mode opens filled in with the process' own anchor record, the
// same mechanism 4.64.1 wired into sp_form_viewer's recordContext.
const formUrl = (formId: string, formMode: string | null | undefined, anchor: FlussAnchor | null | undefined) => {
    const recordId = formMode === "update" ? anchor?.record_id : null;
    return `/g/service-portal/form/${encodeURIComponent(formId)}?embed=1${recordId ? `&recordId=${encodeURIComponent(recordId)}` : ""}`;
};

const isApprovalTask = (task: { node_type?: string | null } | undefined) =>
    task?.node_type === "approval";

const FlussTaskPage = () => {
    const { t } = useTranslation();
    const { taskId = "" } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const notification = useNotification();
    const { data: allColumns = [] } = useColumns();
    const { data: tables = [] } = useTables();
    const [outcome, setOutcome] = useState<string | null>(null);
    const [comment, setComment] = useState("");
    const task = useQuery({
        queryKey: ["fluss", "tasks", taskId],
        queryFn: () => flussApi.getTask(taskId),
        enabled: Boolean(taskId),
    });
    const complete = useMutation({
        mutationFn: (selected: string) => isApprovalTask(task.data)
            ? flussApi.completeTask(taskId, selected, { comment: comment.trim() })
            : flussApi.completeTask(taskId, selected),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["fluss", "tasks"], exact: true, refetchType: "all" });
            navigate("/home/fluss/inbox");
        },
    });

    const recordTarget = task.data?.work_record || task.data?.anchor;
    const conditionColumns = useMemo(() => {
        const table = tables.find((candidate) => candidate.name === recordTarget?.table);
        return allColumns.filter((column) => column.sys_table_id === table?.id);
    }, [allColumns, tables, recordTarget?.table]);
    const choiceLabelMaps = useChoiceLabelMaps(conditionColumns);
    const conditionSummary = useMemo(() => {
        if (!task.data?.completion_condition) return "";
        const dynamicColumns = conditionColumns.flatMap((column) =>
            resolveConditionColumns(column, {}, conditionColumns, allColumns, tables));
        return formatConditionSummary(task.data.completion_condition, conditionColumns, t, choiceLabelMaps, tables, [...allColumns, ...dynamicColumns]);
    }, [task.data?.completion_condition, conditionColumns, choiceLabelMaps, allColumns, tables, t]);

    useEffect(() => {
        if (!recordTarget || !task.data?.completion_condition) return;
        let checking = false;
        return recordMutationBus.subscribe((event) => {
            if (checking || event.table !== recordTarget.table || (event.ids?.length && !event.ids.includes(recordTarget.record_id))) return;
            checking = true;
            void flussApi.getTask(taskId).then(async (latest) => {
                queryClient.setQueryData(["fluss", "tasks", taskId], latest);
                if (latest.status === "completed") {
                    notification.success("FLUSS-TASK-COMPLETED", String(t("fluss.task.completed_after_save", { defaultValue: "Tarefa concluída após salvar o registro." })));
                    await queryClient.invalidateQueries({ queryKey: ["fluss", "tasks"], exact: true, refetchType: "all" });
                    navigate("/home/fluss/inbox");
                }
            }).finally(() => { checking = false; });
        });
    }, [recordTarget?.table, recordTarget?.record_id, task.data?.completion_condition, taskId, queryClient, navigate, notification, t]);

    useFlussCable({
        instanceId: task.data?.instance.id,
        onReceived: () => queryClient.invalidateQueries({ queryKey: ["fluss", "tasks", taskId] }),
    });

    if (!task.data) {
        return <Box className="fluss-page" />;
    }

    const anchor = task.data.anchor;
    const isApproval = isApprovalTask(task.data);
    const commentRequired = outcome === "reject" || outcome === "more_info";
    const error = complete.error ? flussApiError(complete.error) : null;
    const actionTitles: Record<string, string> = {
        approve: String(t("fluss.task.confirm_approve", { defaultValue: "Aprovar tarefa" })),
        reject: String(t("fluss.task.confirm_reject", { defaultValue: "Rejeitar tarefa" })),
        more_info: String(t("fluss.task.confirm_more_info", { defaultValue: "Solicitar mais informações" })),
    };

    return (
        <Box className="fluss-page">
            <Stack gap={2}>
                <FlussPageHeader
                    title={task.data.title}
                    subtitle={flussDefinitionLabel(t, task.data.definition_name)}
                    status={<FlussStatusChip status={task.data.status} />}
                    actions={
                        <>
                            {task.data.allowed_outcomes.map((value) => (
                                <Button key={value} variant="contained" onClick={() => { setComment(""); setOutcome(value); }}>
                                    {t(`fluss.outcome.${value}`, { defaultValue: value })}
                                </Button>
                            ))}
                        </>
                    }
                />
                {error && <Alert severity="error">{error.message}</Alert>}
                {task.data.completion_condition && conditionSummary && <Alert severity="info">
                    {t("fluss.task.completion_condition", { summary: conditionSummary, defaultValue: "Esta tarefa é concluída quando o registro atender a: {{summary}}" })}
                </Alert>}
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack gap={0.5}>
                        <Typography variant="overline">
                            {t("fluss.task.request_context", { defaultValue: "Request context" })}
                        </Typography>
                        <Typography>
                            {t("fluss.task.requester", { name: task.data.requester?.name || "—", defaultValue: "Requested by {{name}}" })}
                        </Typography>
                    </Stack>
                </Paper>
                {anchor?.display && <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline">{t("fluss.task.anchor_record", { defaultValue: "Linked record" })}</Typography>
                    <Typography>{anchor.display}</Typography>
                </Paper>}
                {task.data.instructions?.trim() && <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline">{t("fluss.task.manual_instructions", { defaultValue: "Instructions" })}</Typography>
                    <Typography whiteSpace="pre-wrap">{resolveDisplayText(t, task.data.instructions)}</Typography>
                </Paper>}
                {Boolean(task.data.record_summary?.length) && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack component="dl" gap={1}>
                            {(task.data.record_summary || []).map((field, index) => (
                                <Box key={`${field.label}-${index}`}>
                                    <Typography component="dt" variant="body2" color="text.secondary">{field.label}</Typography>
                                    <Typography component="dd" sx={{ m: 0 }}>{field.value}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                )}
                {isApproval ? null : task.data.node_type === "manual_task" && task.data.description?.trim() ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack gap={0.75}>
                            <Typography variant="overline">
                                {t("fluss.task.manual_instructions", { defaultValue: "Instructions" })}
                            </Typography>
                            <Typography whiteSpace="pre-wrap">
                                {resolveDisplayText(t, task.data.description || "")}
                            </Typography>
                        </Stack>
                    </Paper>
                ) : task.data.form_id ? (
                    <Paper variant="outlined" sx={{ height: "70vh", overflow: "hidden" }}>
                        <iframe
                            title={String(t("fluss.task.form_frame_title", { defaultValue: "Task form" }))}
                            src={formUrl(task.data.form_id, task.data.form_mode, anchor)}
                            style={{ width: "100%", height: "100%", border: "none" }}
                        />
                    </Paper>
                ) : recordTarget ? (
                    <RecordProducer tableName={recordTarget.table} sysId={recordTarget.record_id} explainTaskAccess />
                ) : (
                    <Alert severity="info">
                        {t("fluss.task.no_anchor", { defaultValue: "This task has no linked business record." })}
                    </Alert>
                )}
            </Stack>
            <ConfirmActionDialog
                open={Boolean(outcome)}
                title={task.data.node_type === "manual_task"
                    ? t("fluss.task.confirm_title", { defaultValue: "Concluir tarefa" })
                    : actionTitles[outcome || ""] || t("fluss.task.confirm_action", { action: t(`fluss.outcome.${outcome}`, { defaultValue: outcome || "" }), defaultValue: "Confirmar {{action}}" })}
                description={t("fluss.task.confirm_description", { defaultValue: "This action advances the process and cannot be undone." })}
                confirmLabel={t("fluss.common.confirm", { defaultValue: "Confirm" })}
                cancelLabel={t("fluss.common.cancel", { defaultValue: "Cancel" })}
                busy={complete.isPending}
                confirmDisabled={isApproval && commentRequired && !comment.trim()}
                onClose={() => setOutcome(null)}
                onConfirm={() => outcome && complete.mutate(outcome)}
            >
                {isApproval && <TextField
                    label={String(t("fluss.task.comment", { defaultValue: "Comment" }))}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    required={commentRequired}
                    fullWidth multiline minRows={2} sx={{ mt: 2 }}
                />}
            </ConfirmActionDialog>
        </Box>
    );
};

export default FlussTaskPage;
